import Slider from '@mui/material/Slider';
import axios from 'axios';
import { Loader2, Rocket } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../App.css';
import money from '../assets/icon/money.svg';
import targeting from '../assets/icon/targeting.svg';
import Accordion from '../components/Accordion';
import { Button } from '../components/metronic/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/metronic/ui/Card';
import { Input } from '../components/metronic/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/metronic/ui/Select';
import { Badge } from '../components/metronic/ui/Badge';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';

/** Prefilled example so users can try the flow in one click (form still requires JD + goal). */
const EXAMPLE_CAMPAIGN = {
    jobTitle: 'Software Engineer',
    goal:
        'Find 10 software engineers with 5+ years experience in Python and React, evaluate them with a minimum score of 70, and schedule interviews for the top candidates.',
    jobDescription: `Role: Software Engineer

We are looking for engineers with 5+ years of experience building web applications.

Requirements:
- Strong Python and React skills
- Comfortable with APIs, testing, and code review
- Experience deploying to cloud environments (e.g. AWS) is a plus

This is a full-time role; remote candidates in compatible time zones are welcome.`,
    skills: 'Python, React',
    positions: 10,
    minEvaluationScore: 70,
};

/**
 * Autonomous Campaign Page - Screen 1: Campaign Form
 *
 * Design Pattern: Split-screen (left form, right help)
 * Inspiration: Applicants Management interface
 *
 * This is the entry point for the autonomous campaign flow where users:
 * 1. Fill out campaign form (job title, goal, targeting settings)
 * 2. Submit to start conversational refinement
 * 3. Proceed to execution plan review
 */
function AutonomousCampaign() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentUser, userProfile } = useAuth();

    const [campaignData, setCampaignData] = useState({
        jobTitle: '',
        jobDescription: '', // Job description (paste or upload)
        goal: '', // Natural language goal input
        location: '',
        experience: [0, 20],
        contractType: 'Full-time',
        skills: '',
        budget: '',
        positions: 0,
        minEvaluationScore: 70,
        // Auto-filled or optional
        jobId: '', // Optional - can be empty or from existing job context
        companyId: '', // Optional - auto-filled from user profile
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [jobDescriptionSource, setJobDescriptionSource] = useState('paste'); // 'paste' or 'upload'

    // Auto-fill company_id and job_id from context
    useEffect(() => {
        // Get job_id from URL params if navigating from job board
        const jobIdFromUrl = searchParams.get('job_id');
        if (jobIdFromUrl) {
            setCampaignData(prev => ({ ...prev, jobId: jobIdFromUrl }));
        }

        // Auto-fill company_id from profile/session. Prefer explicit client_id when available.
        const resolvedCompanyId =
            userProfile?.client_id ||
            userProfile?.user_id ||
            currentUser?.uid;
        if (resolvedCompanyId) {
            setCampaignData(prev => (prev.companyId ? prev : { ...prev, companyId: resolvedCompanyId }));
        }
    }, [searchParams, currentUser, userProfile]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCampaignData(prevState => ({
            ...prevState,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleExperienceSlider = (event, newValue) => {
        setCampaignData(prevState => ({
            ...prevState,
            experience: newValue
        }));
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Only accept text-based and document files
        const allowedTypes = [
            'text/plain',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        const fileName = file.name.toLowerCase();
        const isAllowed =
            allowedTypes.includes(file.type) ||
            fileName.endsWith('.txt') ||
            fileName.endsWith('.pdf') ||
            fileName.endsWith('.doc') ||
            fileName.endsWith('.docx');

        if (!isAllowed) {
            setErrors(prev => ({ ...prev, jobDescription: 'Please upload a text, PDF, or Word document' }));
            return;
        }

        // For PDF / Word files, use existing backend upload & parsing pipeline
        const isStructuredUpload =
            fileName.endsWith('.pdf') || fileName.endsWith('.doc') || fileName.endsWith('.docx');

        if (isStructuredUpload) {
            try {
                setErrors(prev => ({ ...prev, jobDescription: '' }));

                const user = auth.currentUser;
                if (!user) {
                    setErrors(prev => ({
                        ...prev,
                        jobDescription: 'You must be logged in to upload a job description file.'
                    }));
                    return;
                }

                const idToken = await user.getIdToken();
                const formData = new FormData();
                formData.append('file', file);

                const response = await axios.post(`${API_BASE_URL}/upload_job_description`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${idToken}`
                    }
                });

                const data = response.data || {};

                if (data.error || data.success === false) {
                    const message =
                        data.error ||
                        'Failed to process job description. Please check your credits or try again.';
                    setErrors(prev => ({ ...prev, jobDescription: message }));
                    return;
                }

                // Use parsed description and job metadata from backend
                setCampaignData(prevState => ({
                    ...prevState,
                    jobDescription: data.description || data.extracted_text || prevState.jobDescription,
                    jobTitle: prevState.jobTitle || data.job_title || prevState.jobTitle,
                    jobId: data.job_id || prevState.jobId
                }));

                if (errors.jobDescription) {
                    setErrors(prev => ({ ...prev, jobDescription: '' }));
                }
            } catch (error) {
                console.error('Error uploading job description file:', error);
                if (error.response?.status === 402) {
                    setErrors(prev => ({
                        ...prev,
                        jobDescription: 'Insufficient credits to upload job description.'
                    }));
                } else {
                    setErrors(prev => ({
                        ...prev,
                        jobDescription:
                            'Failed to process job description file. Please try again or paste the text instead.'
                    }));
                }
            }

            return;
        }

        // For plain text files, read directly in the browser
        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            setCampaignData(prevState => ({
                ...prevState,
                jobDescription: content
            }));
            if (errors.jobDescription) {
                setErrors(prev => ({ ...prev, jobDescription: '' }));
            }
        };
        reader.onerror = () => {
            setErrors(prev => ({ ...prev, jobDescription: 'Error reading file' }));
        };

        reader.readAsText(file);
    };

    const applyExampleCampaign = () => {
        setCampaignData((prev) => ({
            ...prev,
            jobTitle: EXAMPLE_CAMPAIGN.jobTitle,
            goal: EXAMPLE_CAMPAIGN.goal,
            jobDescription: EXAMPLE_CAMPAIGN.jobDescription,
            skills: EXAMPLE_CAMPAIGN.skills,
            positions: EXAMPLE_CAMPAIGN.positions,
            minEvaluationScore: EXAMPLE_CAMPAIGN.minEvaluationScore,
        }));
        setJobDescriptionSource('paste');
        setErrors((prev) => ({
            ...prev,
            jobTitle: '',
            jobDescription: '',
            goal: '',
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!campaignData.jobTitle.trim()) {
            newErrors.jobTitle = 'Job title is required';
        }

        if (!campaignData.jobDescription.trim()) {
            newErrors.jobDescription = 'Job description is required';
        }

        if (!campaignData.goal.trim()) {
            newErrors.goal = 'Campaign goal is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStartCampaign = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepare form_data according to API specification
            const formData = {
                job_title: campaignData.jobTitle,
                job_description: campaignData.jobDescription,
                goal: campaignData.goal,
                location: campaignData.location || undefined,
                experience_min: campaignData.experience[0] || undefined,
                experience_max: campaignData.experience[1] || undefined,
                contract_type: campaignData.contractType || undefined,
                required_skills: campaignData.skills ? campaignData.skills.split(',').map(s => s.trim()).filter(s => s) : undefined,
                budget: campaignData.budget || undefined,
                positions: campaignData.positions || undefined,
                min_evaluation_score: campaignData.minEvaluationScore || undefined,
                // Optional IDs - can be empty strings
                job_id: campaignData.jobId || '',
                company_id: campaignData.companyId || '',
            };

            // Remove undefined values
            Object.keys(formData).forEach(key => {
                if (formData[key] === undefined) {
                    delete formData[key];
                }
            });

            // Call autonomous campaign API
            const apiUrl = `${API_BASE_URL}/api/campaigns/autonomous/start`;
            console.log('Calling API:', apiUrl);
            console.log('Request payload:', { form_data: formData, skip_refinement: false });

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    form_data: formData,
                    skip_refinement: false
                }),
            });

            // Read response as text first (so we can inspect it)
            const responseText = await response.text();
            const contentType = response.headers.get('content-type');
            let data;

            // Check if response is JSON
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Non-JSON response received:', {
                    status: response.status,
                    statusText: response.statusText,
                    contentType: contentType,
                    url: `${API_BASE_URL}/api/campaigns/autonomous/start`,
                    responsePreview: responseText.substring(0, 500)
                });
                throw new Error(`Server returned ${response.status} ${response.statusText}. Expected JSON but got ${contentType || 'unknown'}. Is the backend server running at ${API_BASE_URL}?`);
            }

            // Try to parse as JSON
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('Failed to parse JSON response:', {
                    error: jsonError,
                    status: response.status,
                    responsePreview: responseText.substring(0, 500)
                });
                throw new Error(`Invalid JSON response from server. Status: ${response.status}. Response: ${responseText.substring(0, 200)}`);
            }

            if (response.ok && data.session_id) {
                const status = data.status || 'refining';

                if (status === 'refining') {
                    // Prepare initial question payload
                    const questionPayload = data.conversation_context?.message
                        ? {
                            message: data.conversation_context.message,
                            jobTitle: campaignData.jobTitle,
                            goal: campaignData.goal,
                            questionType: data.conversation_context.question_type,
                          }
                        : null;

                    // Store initial question and context for CampaignRefinement (for refresh safety)
                    if (questionPayload) {
                        localStorage.setItem(
                            `campaign_question_${data.session_id}`,
                            JSON.stringify(questionPayload)
                        );
                    }

                    // Navigate to conversational refinement (Screen 2), passing question via route state
                    navigate(`/campaigns/autonomous/${data.session_id}/refine`, {
                        state: questionPayload ? { questionData: questionPayload } : undefined,
                    });
                } else if (status === 'ready_to_execute') {
                    // No refinement needed – go straight to execution plan review
                    if (data.goal_spec) {
                        // Optionally stash goal_spec for plan screen if needed later
                        localStorage.setItem(
                            `refined_goal_${data.session_id}`,
                            JSON.stringify({
                                natural_language_goal: data.goal_spec.natural_language_goal,
                                job_title: data.goal_spec.job_title,
                                conversation_summary: data.goal_spec.natural_language_goal
                            })
                        );
                    }
                    navigate(`/campaigns/autonomous/${data.session_id}/plan`);
                } else {
                    // Fallback to refinement if we get an unexpected status
                    navigate(`/campaigns/autonomous/${data.session_id}/refine`);
                }
            } else {
                throw new Error(data.error || 'Failed to start campaign');
            }
        } catch (error) {
            console.error('Failed to start campaign:', error);
            alert(`Failed to start campaign: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-start justify-between gap-4">
                        <div className="max-w-3xl">
                            <h1 className="text-2xl font-bold text-gray-900">Create Autonomous Campaign</h1>
                            <p className="text-sm text-gray-700 mt-1.5">
                                ALICE drafts a plan from your job and goal: a short chat to clarify, then an execution
                                plan you approve before the campaign runs.
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                When you click Start Campaign, you go to refinement (and then plan review)—not instant
                                bulk outreach.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content - Split Screen */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex gap-6">
                    {/* Left Panel - Form */}
                    <div className="w-1/2">
                        <Card>
                            <CardHeader>
                                <CardTitle>Campaign Details</CardTitle>
                                <CardDescription>
                                    Add your job, description, and goal. Use the example on the right if you want a
                                    one-click sample.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                        <form onSubmit={handleStartCampaign}>
                            {/* Section 1: Job Information */}
                            <div className="mb-8">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Job Information</h2>

                                {/* Job Title */}
                                <div className="mb-6">
                                    <label htmlFor="jobTitle" className="block text-sm font-semibold text-gray-800 mb-2">
                                        Job Title *
                                    </label>
                                    <Input
                                        type="text"
                                        id="jobTitle"
                                        name="jobTitle"
                                        placeholder="e.g., Software Engineer, Marketing Manager"
                                        value={campaignData.jobTitle}
                                        onChange={handleInputChange}
                                        className={errors.jobTitle ? 'border-red-500' : ''}
                                        required
                                    />
                                    {errors.jobTitle && (
                                        <p className="mt-1 text-sm text-red-600">{errors.jobTitle}</p>
                                    )}
                                </div>

                                {/* Job Description */}
                                <div className="mb-6">
                                    <label htmlFor="jobDescription" className="block text-sm font-semibold text-gray-800 mb-2">
                                        Job Description *
                                    </label>

                                    {/* Toggle between paste and upload */}
                                    <div className="flex gap-2 mb-3">
                                        <Button
                                            type="button"
                                            variant={jobDescriptionSource === 'paste' ? 'primary' : 'outline'}
                                            size="sm"
                                            onClick={() => setJobDescriptionSource('paste')}
                                        >
                                            📋 Paste
                                        </Button>
                                        <Button
                                            type="button"
                                            variant={jobDescriptionSource === 'upload' ? 'primary' : 'outline'}
                                            size="sm"
                                            onClick={() => setJobDescriptionSource('upload')}
                                        >
                                            📁 Upload
                                        </Button>
                                    </div>

                                    {jobDescriptionSource === 'paste' ? (
                                        <textarea
                                            id="jobDescription"
                                            name="jobDescription"
                                            rows={10}
                                            placeholder="Paste the full job description here..."
                                            value={campaignData.jobDescription}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-3 border rounded-lg text-sm resize-none bg-background border-input shadow-xs shadow-black/5 transition-[color,box-shadow] text-foreground placeholder:text-muted-foreground/80 focus-visible:ring-ring/30 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] ${errors.jobDescription ? 'border-red-500' : ''}`}
                                            required
                                        />
                                    ) : (
                                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                                            <input
                                                type="file"
                                                id="jobDescriptionFile"
                                                accept=".txt,.pdf,.doc,.docx"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                            />
                                            <label
                                                htmlFor="jobDescriptionFile"
                                                className="cursor-pointer flex flex-col items-center"
                                            >
                                                <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                <span className="text-sm font-medium text-gray-700">
                                                    Click to upload job description
                                                </span>
                                                <span className="text-xs text-gray-500 mt-1">
                                                    Supports: TXT, PDF, DOC, DOCX
                                                </span>
                                            </label>
                                            {campaignData.jobDescription && (
                                                <p className="mt-2 text-sm text-green-600">
                                                    ✓ File loaded ({campaignData.jobDescription.length} characters)
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {errors.jobDescription && (
                                        <p className="mt-1 text-sm text-red-600">{errors.jobDescription}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">
                                        Provide a detailed job description to help ALICE find better candidates
                                    </p>
                                </div>
                            </div>

                            {/* Section 2: Campaign Goal */}
                            <div className="mb-8">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Campaign Goal</h2>

                                {/* Natural Language Goal Input */}
                                <div className="mb-6">
                                    <label htmlFor="goal" className="block text-sm font-semibold text-gray-800 mb-2">
                                        Campaign Goal *
                                    </label>
                                    <textarea
                                        id="goal"
                                        name="goal"
                                        rows={4}
                                        placeholder="e.g., Find 10 software engineers with 5+ years experience and schedule interviews"
                                        value={campaignData.goal}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-3 border rounded-lg text-sm resize-none bg-background border-input shadow-xs shadow-black/5 transition-[color,box-shadow] text-foreground placeholder:text-muted-foreground/80 focus-visible:ring-ring/30 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] ${errors.goal ? 'border-red-500' : ''}`}
                                        required
                                    />
                                    {errors.goal && (
                                        <p className="mt-1 text-sm text-red-600">{errors.goal}</p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">
                                        Describe what you want ALICE to accomplish in natural language
                                    </p>
                                </div>
                            </div>

                            {/* Section 3: Targeting Settings */}
                            <div className="mb-6">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Targeting Settings</h2>
                                <Accordion open={true} title="Targeting Settings" icon={targeting}>
                                    <div className="flex flex-col gap-4 p-4">
                                        {/* Location */}
                                        <div className="flex flex-col gap-2">
                                            <label className="font-semibold text-gray-800 text-sm">Location</label>
                                            <Input
                                                type="text"
                                                name="location"
                                                placeholder="Enter location (e.g., New York, Remote)"
                                                value={campaignData.location}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        {/* Experience Slider */}
                                        <div className="flex flex-col gap-2">
                                            <label className="font-semibold text-gray-800 text-sm">
                                                Experience ({campaignData.experience[0]}-{campaignData.experience[1]} years)
                                            </label>
                                            <Slider
                                                value={campaignData.experience}
                                                onChange={handleExperienceSlider}
                                                valueLabelDisplay="auto"
                                                min={0}
                                                max={20}
                                                style={{ color: "#F7931D" }}
                                            />
                                        </div>

                                        {/* Contract Type */}
                                        <div className="flex flex-col gap-2">
                                            <label className="font-semibold text-gray-800 text-sm">Contract Type</label>
                                            <Select
                                                value={campaignData.contractType}
                                                onValueChange={(value) => setCampaignData(prev => ({ ...prev, contractType: value }))}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select contract type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Full-time">Full-Time</SelectItem>
                                                    <SelectItem value="Part-time">Part-Time</SelectItem>
                                                    <SelectItem value="Contract">Contract</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Skills */}
                                        <div className="flex flex-col gap-2">
                                            <label className="font-semibold text-gray-800 text-sm">
                                                Skills
                                                <span className="text-gray-500 font-normal ml-1">(separate with commas)</span>
                                            </label>
                                            <Input
                                                type="text"
                                                name="skills"
                                                placeholder="e.g., Python, React, AWS"
                                                value={campaignData.skills}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        {/* Evaluation Threshold */}
                                        <div className="flex flex-col gap-2">
                                            <label className="font-semibold text-gray-800 text-sm">
                                                Minimum Evaluation Score
                                                <span className="text-gray-500 font-normal ml-1">(0-100, used for shortlisting)</span>
                                            </label>
                                            <Input
                                                type="number"
                                                name="minEvaluationScore"
                                                min="0"
                                                max="100"
                                                placeholder="e.g., 80"
                                                value={campaignData.minEvaluationScore}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                </Accordion>
                            </div>

                            {/* Budget Management - Collapsible */}
                            <div className="mb-6">
                                <Accordion open={false} title="Budget Management" icon={money}>
                                    <div className="flex flex-col gap-4 p-4">
                                        <div className="flex flex-col gap-2">
                                            <label className="font-semibold text-gray-800 text-sm">Budget</label>
                                            <Input
                                                type="text"
                                                name="budget"
                                                placeholder="Enter budget (optional)"
                                                value={campaignData.budget}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                </Accordion>
                            </div>

                            {/* Number of Positions */}
                            <div className="mb-6">
                                <label htmlFor="positions" className="block text-sm font-semibold text-gray-800 mb-2">
                                    Number of Positions
                                </label>
                                <Input
                                    type="number"
                                    id="positions"
                                    name="positions"
                                    min="0"
                                    placeholder="e.g., 10"
                                    value={campaignData.positions}
                                    onChange={handleInputChange}
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={isSubmitting}
                                className="w-full"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Starting Campaign...
                                    </>
                                ) : (
                                    <>
                                        <Rocket className="w-4 h-4 mr-2" />
                                        Start Campaign
                                    </>
                                )}
                            </Button>
                        </form>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Panel - Help & Examples */}
                    <div className="w-1/2">
                        <Card className="sticky top-6">
                            <CardHeader>
                                <CardTitle>What you get with ALICE</CardTitle>
                                <CardDescription>
                                    A guided setup, a clear plan, and your approval before autonomous steps run.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>

                            {/* Example Goal */}
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">Example campaign:</h3>
                                <Card variant="accent">
                                    <CardContent className="space-y-3">
                                        <p className="text-sm text-gray-700">
                                            &quot;Find 10 software engineers with 5+ years experience in Python and React,
                                            evaluate them with a minimum score of 70, and schedule interviews for the top
                                            candidates.&quot;
                                        </p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="w-full sm:w-auto"
                                            onClick={applyExampleCampaign}
                                        >
                                            Use this example
                                        </Button>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Tips */}
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">💡 Tips:</h3>
                                <ul className="space-y-2 text-sm text-gray-600">
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        <span>Be specific about requirements (years of experience, skills, location)</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        <span>Include target numbers (e.g., "find 10 candidates")</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        <span>Mention evaluation criteria (e.g., "score above 70")</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        <span>Specify desired outcomes (e.g., "schedule interviews")</span>
                                    </li>
                                </ul>
                            </div>

                            {/* How It Works */}
                            <div className="border-t border-gray-100 pt-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">How It Works:</h3>
                                <div className="space-y-3 text-sm text-gray-600">
                                    <div className="flex items-start">
                                        <Badge variant="primary" size="sm" className="mr-2">1</Badge>
                                        <span>Fill out the form with your job requirements and goal</span>
                                    </div>
                                    <div className="flex items-start">
                                        <Badge variant="primary" size="sm" className="mr-2">2</Badge>
                                        <span>ALICE will ask clarifying questions to refine your requirements</span>
                                    </div>
                                    <div className="flex items-start">
                                        <Badge variant="primary" size="sm" className="mr-2">3</Badge>
                                        <span>Review and approve the execution plan</span>
                                    </div>
                                    <div className="flex items-start">
                                        <Badge variant="primary" size="sm" className="mr-2">4</Badge>
                                        <span>ALICE executes the campaign autonomously</span>
                                    </div>
                                    <div className="flex items-start">
                                        <Badge variant="primary" size="sm" className="mr-2">5</Badge>
                                        <span>Monitor progress and review results</span>
                                    </div>
                                </div>
                                <p className="mt-4 text-sm text-gray-600 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                                    You stay in control: review and approve the execution plan before ALICE runs the
                                    campaign autonomously. You can pause or follow progress from the campaign screens.
                                </p>
                            </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AutonomousCampaign;

